import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { LabelM } from '@sorare/core/src/atoms/typography';
import TeamAvatar from '@sorare/core/src/components/club/TeamAvatar';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { MinimalistGameScore } from './MinimalistGameScore';
import { MinimalistGame_Game } from './__generated__/index.graphql';

const Root = styled.div`
  display: grid;
  grid-template-columns: 1fr max-content 1fr;
  column-gap: var(--double-unit);
`;
const TeamWrapper = styled(LabelM)`
  display: flex;
  flex-direction: column;
  gap: var(--unit);
  align-items: center;
  text-align: center;

  @media ${tabletAndAbove} {
    &.expanded {
      flex-direction: row;
      gap: var(--double-unit);
      align-items: center;
      justify-content: flex-start;
    }
  }
`;
const HomeTeamWrapper = styled(TeamWrapper)`
  @media ${tabletAndAbove} {
    &.expanded {
      flex-direction: row-reverse;
    }
  }
`;

type Props = {
  game: MinimalistGame_Game;
  expanded?: boolean;
  hideLabels?: boolean;
};

export const MinimalistGame = ({ game, expanded, hideLabels }: Props) => {
  const { homeTeam, awayTeam } = game;

  return (
    <Root>
      <HomeTeamWrapper as="div" className={classNames({ expanded })}>
        <TeamAvatar team={homeTeam} size={32} />
        {!hideLabels && homeTeam?.shortName}
      </HomeTeamWrapper>
      <MinimalistGameScore game={game} expanded={expanded} />
      <TeamWrapper as="div" className={classNames({ expanded })}>
        <TeamAvatar team={awayTeam} size={32} />
        {!hideLabels && awayTeam?.shortName}
      </TeamWrapper>
    </Root>
  );
};

MinimalistGame.fragments = {
  game: gql`
    fragment MinimalistGame_Game on Game {
      id
      homeTeam {
        slug
        code
        shortName
        ...TeamAvatar_team
      }
      awayTeam {
        slug
        code
        shortName
        ...TeamAvatar_team
      }
      ...MinimalistGameScore_game
    }
    ${TeamAvatar.fragments.team}
    ${MinimalistGameScore.fragments.game}
  ` as TypedDocumentNode<MinimalistGame_Game>,
};
